// React loaded

// Локальные шрифты для оптимизации LCP — Swiss Modernism: Inter + JetBrains Mono
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/inter/800.css';
import '@fontsource/inter/900.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import '@fontsource/jetbrains-mono/600.css';
import '@fontsource/jetbrains-mono/700.css';

// Должен выполниться до создания первой Zod-схемы — см. комментарий в модуле
import '@schemas/zodConfig';

import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';

import { App } from '@app/main/App';
import {
  isChunkLoadError,
  tryChunkReload,
} from '@utils/helpers/chunkErrorHandler';
import {
  readTransportCause,
  TRANSPORT_TAG,
} from '@utils/helpers/transportCause';

/**
 * Обработка stale chunks после деплоя (AI-43, AI-76).
 *
 * 1. `vite:preloadError` — Chrome и браузеры с Vite preload-хелпером.
 * 2. `unhandledrejection` — Safari, Firefox, где Vite-событие не срабатывает.
 *
 * Защита от бесконечного цикла: максимум 1 reload за 10 секунд.
 */
globalThis.addEventListener('vite:preloadError', () => {
  tryChunkReload();
});

globalThis.addEventListener(
  'unhandledrejection',
  (event: PromiseRejectionEvent) => {
    if (!isChunkLoadError(event.reason)) {
      return;
    }

    event.preventDefault();
    tryChunkReload();
  },
);

/**
 * CarrotQuest/Dashly SDK иногда бросает ошибки из setTimeout-колбэков
 * (например, «Error invoking postEvent: Method not found» на мобильных браузерах).
 * Sentry перехватывает их через свой wrapper раньше window 'error',
 * поэтому фильтрация на уровне beforeSend — единственный способ не терять
 * видимость, но убрать шум из основного потока ошибок.
 *
 * Стратегия: понижаем level до warning + тег source:carrotquest.
 * Для мониторинга «CQ сломался» — alert rule на спайк warnings с этим тегом.
 */
const CARROT_QUEST_ERROR_PATTERNS = [
  'postevent',
  'carrotquest',
  'dashly',
  'cdn.carrotquest.app',
];

const isCarrotQuestError = (event: Sentry.ErrorEvent): boolean => {
  const exceptions = event.exception?.values ?? [];

  for (const exception of exceptions) {
    const message = exception.value?.toLowerCase() ?? '';
    if (CARROT_QUEST_ERROR_PATTERNS.some((pattern) => message.includes(pattern))) {
      return true;
    }

    const frames = exception.stacktrace?.frames ?? [];
    for (const frame of frames) {
      const filename = frame.filename?.toLowerCase() ?? '';
      if (CARROT_QUEST_ERROR_PATTERNS.some((pattern) => filename.includes(pattern))) {
        return true;
      }
    }
  }

  return false;
};

if (import.meta.env.MODE === 'production') {
  // ✅ Top-level await
  const { router } = await import('@navigation/router');
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENVIROMENT,
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/\w+\.iidf\.ru\/api/,
      /^https:\/\/\w+\.example\.com\/api/,
    ],
    normalizeDepth: 5,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1,
    beforeSend(event, hint) {
      if (event.exception) {
        const isChunk = event.exception.values?.some((exception) =>
          isChunkLoadError(exception.value ?? ''),
        );
        if (isChunk) {
          event.tags = { ...event.tags, source: 'chunk-load-error' };
        }

        if (isCarrotQuestError(event)) {
          event.level = 'warning';
          event.tags = { ...event.tags, source: 'carrotquest' };
        }

        /*
         * Причина транспорта берётся из ИСХОДНОГО исключения (`hint`), а не из
         * события: в событие она не попадает вовсе — там остаются только имя
         * класса и текст, одинаковые у всех четырёх причин (AF-537).
         */
        const transportCause = readTransportCause(hint?.originalException);
        if (transportCause) {
          event.tags = { ...event.tags, [TRANSPORT_TAG]: transportCause };
        }
      }
      return event;
    },
  });
}

// Components
const AppWithProfiler = Sentry.withProfiler(App);

if (import.meta.env.MODE === 'msw') {
  const module = await import('../test_configs/api_mocks/browser');
  const { worker } = module;
  await worker.start({
    onUnhandledRequest(request, print) {
      const globalRegex = /ts|tsx|js/g;
      if (globalRegex.test(request.url)) {
        return;
      }

      // Otherwise, print an unhandled request warning.
      print.warning();
    },
  });
}

// Application rendered
const container = document.querySelector('#root');
if (container) {
  const root = createRoot(container);
  root.render(<AppWithProfiler />);
}
